import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Package, Heart, LogOut, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const Account = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { 
      icon: Package, 
      label: 'Order History', 
      description: 'View your past orders',
      href: '/orders' 
    },
    { 
      icon: Heart, 
      label: 'Wishlist', 
      description: 'Items you\'ve saved',
      href: '/wishlist' 
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-32 pb-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-3xl p-8 shadow-sm mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-[#D93052] rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1A3C34] font-['Chivo']">
                {user?.name}
              </h1>
              <p className="text-[#6B7280]">{user?.email}</p>
            </div>
          </div>
          
          <div className="bg-[#E8EFE9] rounded-2xl p-4">
            <p className="text-sm text-[#6B7280]">Member since</p>
            <p className="font-medium text-[#1A3C34]">
              {new Date(user?.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long'
              })}
            </p>
          </div>
        </div>

        {/* Menu Items */}
        <div className="bg-white rounded-3xl shadow-sm overflow-hidden mb-6">
          {menuItems.map((item, index) => (
            <Link 
              key={item.label}
              to={item.href}
              className={`flex items-center justify-between p-6 hover:bg-[#F9F9F7] transition-colors ${
                index !== menuItems.length - 1 ? 'border-b border-[#E8EFE9]' : ''
              }`}
              data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#E8EFE9] rounded-xl flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-[#1A3C34]" />
                </div>
                <div>
                  <p className="font-bold text-[#1A3C34]">{item.label}</p>
                  <p className="text-sm text-[#6B7280]">{item.description}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#6B7280]" />
            </Link>
          ))}
        </div>

        {/* Logout Button */}
        <Button 
          onClick={handleLogout}
          variant="outline"
          className="w-full rounded-full border-2 border-red-200 text-red-600 font-bold py-6 hover:bg-red-50 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );
};

export default Account;
